import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Meteor } from 'meteor/meteor';
import { withTracker } from 'meteor/react-meteor-data';
import { findIndex, get, isNil } from 'lodash';
import turf from '@turf/turf';

import * as atlasOps from '../../../api/Atlas/atlas-operations';
import Layers, { LAYER_SUBTYPES } from '../../../api/Layers/Layers';
import * as taskOps from '../../../api/Tasks/task-operations';
import TelemetryConnections from '../../../api/TelemetryConnections/TelemetryConnections';

import focusedTrainTask from '../../../modules/tasks/focusedTrainTask';

import FocusedTrainTaskConfiguration from '../../components/FocusedTrainTaskConfiguration';
import Loading from '../../components/Loading';
import MapContainerFocusedTrainAtlas from '../../containers/MapContainerFocusedTrainAtlas';
import TaskMonitorContainer from '../../containers/TaskMonitorContainer';

import {
  GridContainer,
  Grid,
  GridLeftChild,
  GridRightChild,
  GridTopRightChild,
  GridBottomRightChild,
  GridTaskMonitorContainer
} from './styles';

const PER_PAGE = 5;
const HEADER_HEIGHT = 57; // When it is all in the same line
const PAGINATION_HEIGHT = 46;
const CELL_HEIGHT = 62;

class FocusedTrainAtlas extends Component {
  tableElement = React.createRef();

  state = {
    aois: {
      type: 'FeatureCollection',
      features: []
    },
    entities: {
      type: 'FeatureCollection',
      features: []
    },
    isEntities: true,
    isTableListLoaded: false,
    itemsPerPage: PER_PAGE,
    selectedLayer: '',
    selectedTaskEntities: []
  };

  componentDidMount() {
    window.addEventListener('resize', this.updateDimensions);
  }

  componentDidUpdate() {
    const { isTableListLoaded } = this.state;
    if (!isTableListLoaded && !isNil(this.tableElement.current)) this.updateDimensions();
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.updateDimensions);
  }

  setAIInterestArea = newAIInterestArea => {
    const { isEntities, entities, aois } = this.state;

    if (!isEntities) {
      let newFeatures = aois.features;
      if (
        turf.getType(newAIInterestArea) === 'Polygon' ||
        turf.getType(newAIInterestArea) === 'MultiPolygon'
      ) {
        newFeatures.push(newAIInterestArea);
      } else {
        newFeatures = aois.features.concat(newAIInterestArea.features);
      }
      const newAOIS = turf.featureCollection(newFeatures);

      this.setState({ aois: newAOIS });
    } else {
      let newFeatures = entities.features;
      if (
        turf.getType(newAIInterestArea) === 'Polygon' ||
        turf.getType(newAIInterestArea) === 'MultiPolygon'
      ) {
        newFeatures.push(newAIInterestArea);
      } else {
        newFeatures = entities.features.concat(newAIInterestArea.features);
      }
      const newEntities = turf.featureCollection(newFeatures);
      this.setState({ entities: newEntities });
    }
  };

  updateDimensions = () => {
    const height =
      get(this, 'tableElement.current.offsetHeight', 0) - (PAGINATION_HEIGHT + HEADER_HEIGHT);

    let itemsPerPage = Math.floor(height / CELL_HEIGHT);
    if (itemsPerPage < 1) itemsPerPage = 1;

    this.setState({ isTableListLoaded: true, itemsPerPage });
  };

  updateFlag = newIsEntities => this.setState({ isEntities: newIsEntities });

  updateSelectedLayer = layerId => this.setState({ selectedLayer: layerId });

  render() {
    const {
      atlas,
      deployEnv,
      gmsConnection,
      gridBackgroundColor,
      layers,
      isPreview,
      isPublic,
      loading
    } = this.props;
    const {
      aois,
      entities,
      isEntities,
      itemsPerPage,
      selectedTaskEntities,
      selectedLayer
    } = this.state;

    const outputNodeIndex = findIndex(taskOps.getNodes(focusedTrainTask({})), ['id', 'output']);
    const pathToConnector = `nodes[${outputNodeIndex}].connectionParams.connector`;

    if (loading) return <Loading />;
    return (
      <>
        <GridContainer backgroundcolor={gridBackgroundColor} fullScreen>
          <Grid>
            <GridLeftChild xs={12} md={8}>
              <MapContainerFocusedTrainAtlas
                aois={aois}
                atlas={atlas}
                deployEnv={deployEnv}
                entities={entities}
                setAIInterestArea={this.setAIInterestArea}
                isEntities={isEntities}
                layers={layers}
                selectedLayer={selectedLayer}
              />
            </GridLeftChild>
            <GridRightChild xs={12} md={4}>
              <GridTopRightChild height="50%">
                <FocusedTrainTaskConfiguration
                  aiInterestArea={{}}
                  atlas={atlas}
                  aois={aois}
                  entities={entities}
                  gmsConnection={gmsConnection}
                  isEntities={isEntities}
                  isPreview={isPreview}
                  isPublic={isPublic}
                  itemsPerPage={itemsPerPage}
                  layers={layers}
                  selectedLayer={selectedLayer}
                  selectedTaskEntities={selectedTaskEntities}
                  updateFlag={this.updateFlag}
                  updateSelectedLayer={this.updateSelectedLayer}
                />
              </GridTopRightChild>
              <GridBottomRightChild height="50%">
                <GridTaskMonitorContainer ref={this.tableElement}>
                  <TaskMonitorContainer
                    atlasId={atlasOps.getId(atlas)}
                    gmsConnection={gmsConnection}
                    isAtlas
                    isFocusTrainAtlas
                    isPreview={isPreview}
                    isPublic={isPublic}
                    itemsPerPage={itemsPerPage}
                    pathToConnector={pathToConnector}
                    taskLabel={taskOps.getLabel(focusedTrainTask({}))}
                    selectTask={this.selectTask}
                  />
                </GridTaskMonitorContainer>
              </GridBottomRightChild>
            </GridRightChild>
          </Grid>
        </GridContainer>
      </>
    );
  }
}

FocusedTrainAtlas.defaultProps = {
  atlas: {},
  deployEnv: '',
  gmsConnection: {},
  gridBackgroundColor: '#98c1de',
  layers: [],
  isPreview: false,
  isPublic: false
};

FocusedTrainAtlas.propTypes = {
  atlas: PropTypes.object,
  deployEnv: PropTypes.string,
  gmsConnection: PropTypes.object,
  gridBackgroundColor: PropTypes.string,
  isPreview: PropTypes.bool,
  isPublic: PropTypes.bool,
  layers: PropTypes.array,
  loading: PropTypes.bool.isRequired
};

export default withTracker(({ atlas }) => {
  let gmsConnectionId = '';
  let subscription = {};
  let layersSub = {};

  if (!isNil(atlas)) {
    gmsConnectionId = atlasOps.getGMSConnection(atlas);
    subscription = Meteor.subscribe('gmsConnections.public', gmsConnectionId);
    layersSub = Meteor.isClient && Meteor.subscribe('layers');
  }
  return {
    loading: !isNil(atlas) && !subscription.ready() && !layersSub.ready(),
    gmsConnection: TelemetryConnections.findOne(gmsConnectionId),
    layers: Layers.find({ layerSubType: LAYER_SUBTYPES.WMS }).fetch()
  };
})(FocusedTrainAtlas);
